import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/contexts/AuthContext';
import styles from '@/components/auth/AuthPages.module.css';

export default function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 12) {
      setError('The new password must be at least 12 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to change the password.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className={styles.authPage}>
      <div className={styles.authContent}>
        <div className={styles.brand}>MegaTraderDX</div>
        <Card header={user?.mustChangePassword ? 'Change your temporary password' : 'Change password'}>
          <form className={styles.form} onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}
            {user?.mustChangePassword && (
              <p className={styles.hint}>Choose a new password before continuing to the workspace.</p>
            )}
            <Input
              label="Current password"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <Input
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
            <div className={styles.actions}>
              {user?.mustChangePassword ? (
                <Button type="button" variant="ghost" onClick={() => void logout()}>Sign out</Button>
              ) : (
                <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>
              )}
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Changing...' : 'Change password'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </main>
  );
}
